import { observer } from 'mobx-react-lite';
import { Pressable, StyleSheet, View } from 'react-native';
import { ProductPreview } from '@photosi/rn-components/product-preview/ProductPreview';
import useResponsiveScreen from '../../../hooks/useResponsiveScreen';
import { useStore } from '../../../contexts/StoreContext';
import { ProductModelInstance } from '../../../store/_models/product';
import { useEffect, useMemo, useState } from 'react';
import Datadog from '../../../_shared/datadog/index.web';
import { DATADOG_CUSTOM_ACTIONS } from '../../../_shared/datadog/DatadogCustomActions';
import { ACTIVE_TOOLS } from 'src/store/page-project/pageProject.constants';
import { neutrals } from 'src/theme/colors';
import PreviewUtilsBar from '@photosi/rn-components/product-preview/PreviewUtilsBar';
import { useSizeLimitContext } from '../../../contexts/SizeLimitContext';
import { GridItemContentHeader } from '@photosi/rn-components/product-groups/grid-item-content/grid-item-content-header/GridItemContentHeader';
import { GridItemContentFooter } from '@photosi/rn-components/product-groups/grid-item-content/grid-item-content-footer/GridItemContentFooter';
import { OverlayItem } from '@photosi/rn-components/product-preview/ProductPreview.types';
import { useNavigation } from '@react-navigation/native';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { SharedValue, useSharedValue } from 'react-native-reanimated';
import {
  getFormatI18nKey,
  getFormatVariableI18nKey,
  getPaperI18nKey,
} from 'src/_shared/utils/getI18nKeys';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { ProductOptionsMobilePosition } from '@photosi/rn-components/product-options-mobile/ProductOptionsMobile.types';
import { useMultiSelection } from 'src/hooks/use-multi-selection/useMultiSelection.web';
import { parseAnalyticsParamsByProduct } from 'src/_shared/analytics.utils';
import { logError } from 'src/_shared/logger';
import { productTestID } from 'src/helpers/testIDs.helper';

export type ProductProps = {
  product: ProductModelInstance;
  id: string;
  index: number;
  scrollTo?: (index: number) => void;
  shouldShowPreviewUtilsBar?: boolean;
  shouldUseContainerDimensions?: boolean;
  isGrouped?: boolean;
  isModalEdit?: boolean;
  showOverlays?: SharedValue<boolean> | boolean;
  disabled?: boolean;
  showBadges?: boolean;
  dropdownPosition?: ProductOptionsMobilePosition;
  onHandleMultiple?: (id: string, allowMultiSelect: boolean, wasSelected: boolean) => void;
  onShowOptionMobile?: () => void;
};

export const Product = observer(
  ({
    product,
    id,
    index,
    scrollTo,
    shouldShowPreviewUtilsBar = true,
    shouldUseContainerDimensions = false,
    isGrouped = false,
    isModalEdit = false,
    showOverlays = false,
    disabled = false,
    showBadges = true,
    dropdownPosition = ProductOptionsMobilePosition.LEFT,
    onHandleMultiple,
    onShowOptionMobile,
  }: ProductProps) => {
    const {
      i18n,
      project,
      project: { isEditModeActive, isPreviewModeActive, activeTool, isProductEditModeActive, activeProject },
    } = useStore();
    const { isDesktop, isMobile } = useResponsiveScreen();
    const containerSize = useSizeLimitContext();
    const newRoute = useNavigation().getState()?.routes[0].name === ROUTE_NAMES.EditorNew;
    const { allowMultiSelect } = useMultiSelection([]);
    const isSelected = product.isSelected;
    const [isHover, setIsHover] = useState<boolean>(false);
    const isHighlightedSV = useSharedValue<boolean>(newRoute && !isModalEdit && isSelected);

    useEffect(() => {
      isHighlightedSV.value = newRoute && !isModalEdit && (isHover || isSelected);
    }, [isModalEdit, isHover, isSelected]);

    const handlePress = () => {
      scrollTo && scrollTo(index);
    };
    const select = () => {
      const wasSelected = product.isSelected;
      product.setIsSelected(!wasSelected);
      onHandleMultiple?.(product.id, allowMultiSelect, wasSelected);
      if (!wasSelected) {
        amplitude.trackEvent(AnalyticsEvents.personalize.photoSelectClick, {
          ...parseAnalyticsParamsByProduct(product),
          photo_id: product.id,
          has_quality_alert: product.userImages?.[0]?.qualityAlert,
        });
      }
    };
    const previewOverlayArray = useMemo<OverlayItem[]>(
      () => [
        {
          render: () => <PreviewUtilsBar index={index} product={product} />,
          position: 'top',
        },
      ],
      [index, product],
    );

    const groupedOverlayArray = useMemo<OverlayItem[]>(() => {
      const format = product?.choices?.format;
      const paper = product?.choices?.paper;
      const productFamily = product.family;
      const textFormat = product?.actualFormat
        ? product?.actualFormat + i18n.t(getFormatVariableI18nKey(productFamily))
        : format
          ? i18n.t(getFormatI18nKey(productFamily, format))
          : undefined;

      const textPaper = paper ? i18n.t(getPaperI18nKey(productFamily, paper)) : undefined;

      return [
        {
          render: () => (
            <GridItemContentHeader
              textFormat={textFormat}
              textPaper={textPaper}
              onDuplicate={() =>
                activeProject?.duplicateProduct(product).catch(error => logError('Error duplicating product', error))
              }
              product={product}
              dropdownPosition={dropdownPosition}
              onShowOptionMobile={onShowOptionMobile}
            />
          ),
          position: 'top',
        },
        {
          render: () => {
            return isDesktop && <GridItemContentFooter product={product} />;
          },
          position: 'bottom',
        },
      ];
    }, [
      product,
      product.family,
      product?.choices?.format,
      product?.choices?.paper,
      product.type,
      product.id,
      product.isSelected,
    ]);

    const overlayHandler = useMemo(() => {
      return () => {
        // Caso editor "vecchio"
        if (!newRoute) {
          if (!isEditModeActive) return previewOverlayArray;
          return undefined;
        }

        // Caso preview mode (non edit mode) con barra utils
        if (!isEditModeActive && shouldShowPreviewUtilsBar && !isMobile) return previewOverlayArray;

        // Caso edit mode con barra utils
        if (isEditModeActive && shouldShowPreviewUtilsBar && !isMobile) return previewOverlayArray;

        // Caso grouped (indipendente da edit mode)
        if (isGrouped) return groupedOverlayArray;

        return undefined;
      };
    }, [isEditModeActive, shouldShowPreviewUtilsBar, isMobile, isGrouped, previewOverlayArray, groupedOverlayArray]);

    useEffect(() => {
      if (
        project.activeProject &&
        index === project.activeProject.products.length - 1 &&
        project.activeProject.addingProductCount > 0 &&
        project.activeProject.alreadyAddedProductCount === project.activeProject.addingProductCount
      ) {
        Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.frame_loading_time_with_photos, {
          addedProduct: project.activeProject.alreadyAddedProductCount,
        });
        Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.frame_addition_time);
      }
    }, []);

    const isActive = (isMobile || product.id === project.activeProduct?.id) && (newRoute ? isEditModeActive : true);
    const shownFullScreen = isEditModeActive && ((!newRoute && isMobile) || product.preview.isMultiphoto);

    const onPressHandler = () => {
      if (!isActive && !isGrouped) {
        handlePress();
        return;
      }
      if (!isEditModeActive) {
        select();
        return;
      }
    };
    return (
      <Pressable
        onPress={onPressHandler}
        testID={productTestID(product.id)}
        style={[
          styles.container,
          isGrouped && styles.containerGrouped,
          isMobile ? styles.containerMobile : styles.containerDesktop,
          isActive && styles.disabledPressable,
          isModalEdit && styles.modalEditContainer,
          product.isUpdating && styles.updating,
        ]}
        onHoverIn={() => setIsHover(true)}
        onHoverOut={() => setIsHover(false)}
      >
        {product.preview &&
          (shouldUseContainerDimensions ? (
            <View key={`${id}-Preview-${product.id}`} style={styles.fullSizeWrapper}>
              <ProductPreview
                productType={product.type}
                disabled={disabled || !isActive}
                preview={product.preview}
                editable={isEditModeActive && isActive && !isGrouped}
                showGrid={isEditModeActive && activeTool === ACTIVE_TOOLS.frame && isActive && !isGrouped}
                disableSwapImageAnimation={isEditModeActive && activeTool === ACTIVE_TOOLS.adjustments}
                swappable={isPreviewModeActive || isProductEditModeActive}
                shownFullScreen={shownFullScreen}
                shouldUseContainerDimensions={shouldUseContainerDimensions}
                renderOverlays={overlayHandler}
                showOverlays={showOverlays}
                isHighlighted={isHighlightedSV}
                showBadges={showBadges}
                userTexts={product.userTexts}
              />
            </View>
          ) : (
            <View
              key={`${id}-Preview-${product.id}`}
              style={{
                width: containerSize.width,
                height: containerSize.height,
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <ProductPreview
                productType={product.type}
                disabled={disabled || !isActive}
                preview={product.preview}
                editable={isEditModeActive}
                showGrid={isEditModeActive && activeTool === ACTIVE_TOOLS.frame}
                disableSwapImageAnimation={isEditModeActive && activeTool === ACTIVE_TOOLS.adjustments}
                swappable={isPreviewModeActive || isProductEditModeActive}
                shownFullScreen={shownFullScreen}
                shouldUseContainerDimensions={shouldUseContainerDimensions}
                renderOverlays={overlayHandler}
                showOverlays={showOverlays}
                isHighlighted={isHighlightedSV}
                showBadges={showBadges}
                userTexts={product.userTexts}
              />
            </View>
          ))}
      </Pressable>
    );
  },
);

export const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalEditContainer: {
    width: '100%',
    height: '100%',
  },
  containerGrouped: {
    width: '100%',
    height: '100%',
  },
  fullSizeWrapper: {
    width: '100%',
    height: '100%',
    position: 'relative',
    // borderColor: "red",
    // borderWidth: 2,
  },
  containerDesktop: {},
  containerMobile: {
    paddingHorizontal: 16,
    flexDirection: 'column',
  },
  updating: {
    opacity: 0.5,
    pointerEvents: 'none', // Disable interaction when hidden
  },
  title: {
    textAlign: 'center',
    color: neutrals.neutralGrays.tarmac,
    fontWeight: 400,
    position: 'absolute',
    top: -20,
    fontSize: 15.37,
    lineHeight: 19.78,
    letterSpacing: 0.16,
    minHeight: 20,
  },
  titleDesktop: {
    color: '#7D7D82',
    fontSize: 15,
    lineHeight: 20,
    letterSpacing: -0.24,
  },
  titleMobile: {
    color: neutrals.neutralGrays.tarmac,
    fontSize: 15.37,
    lineHeight: 19.78,
    letterSpacing: 0.16,
  },
  disabledPressable: {
    cursor: 'auto', // Prevents "pointer" cursor
  },
});
